import { useEffect, useRef, useState } from 'react';
import { Typography, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface EllipsisTextProps {
  text: string | null | undefined;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

/**
 * Single-line text that truncates with CSS ellipsis and shows the full text in
 * a tooltip — but only when the text is actually overflowing its container.
 */
export function EllipsisText({ text, placeholder = '—', sx }: EllipsisTextProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  if (!text) {
    return (
      <Typography sx={{ fontWeight: 400, fontSize: '13px', color: 'text.secondary', ...sx }}>
        {placeholder}
      </Typography>
    );
  }

  return (
    <Tooltip
      title={text}
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      <Typography
        ref={ref}
        noWrap
        sx={{ fontWeight: 400, fontSize: '13px', display: 'block', ...sx }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}
